"use client";

import { useState, useEffect, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Button } from "@ui/components/button";
import { Input } from "@ui/components/input";
import { MailIcon, CheckCircleIcon, LoaderIcon, ClockIcon, ExternalLinkIcon } from "lucide-react";
import { fetchReportGenerationEstimate, setReportNotifyEmail } from "../lib/api";
import aiVisibilityStats from "../data/ai-visibility-stats.json";
import { formatTimeRemaining, getEstimatedTimeRemaining, type ReportProgressStep } from "@repo/core/client";

interface AiVisibilityStat {
  title: string;
  quote: string;
  source: string;
  date: string;
  url: string;
}

interface ReportGeneratingPanelProps {
  publicToken: string;
  domain: string;
  status: string;
  userEmail?: string | null; // Pre-fill for logged-in users
  // New progress tracking fields
  progressStep?: string | null;
  progressPercent?: number | null;
  progressMessage?: string | null;
}

// Helper function to get a random index, optionally excluding a specific index
function getRandomStatIndex(excludeIndex?: number): number {
  if (aiVisibilityStats.length === 0) return 0;
  if (aiVisibilityStats.length === 1) return 0;
  
  let newIndex: number;
  do {
    newIndex = Math.floor(Math.random() * aiVisibilityStats.length);
  } while (newIndex === excludeIndex);
  
  return newIndex;
}

export function ReportGeneratingPanel({ 
  publicToken, 
  domain, 
  status,
  userEmail,
  progressStep,
  progressPercent,
  progressMessage,
}: ReportGeneratingPanelProps) {
  const t = useTranslations();
  // Start with a random stat index
  const [currentStatIndex, setCurrentStatIndex] = useState(() => getRandomStatIndex());
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [email, setEmail] = useState(userEmail || "");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Calculate estimated time remaining based on current step
  const estimatedTimeRemaining = progressStep 
    ? getEstimatedTimeRemaining(progressStep as ReportProgressStep)
    : null;
  const timeRemainingDisplay = estimatedTimeRemaining 
    ? formatTimeRemaining(estimatedTimeRemaining)
    : null;

  // Fetch the report generation estimate (fallback if no progress tracking)
  const { data: estimate } = useQuery({
    queryKey: ["report-generation-estimate"],
    queryFn: fetchReportGenerationEstimate,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    // Only fetch if we don't have progress data
    enabled: !progressStep,
  });

  // Mutation to set the notify email
  const notifyMutation = useMutation({
    mutationFn: async (emailToNotify: string) => {
      return setReportNotifyEmail(publicToken, emailToNotify);
    },
    onSuccess: () => {
      setEmailSubmitted(true);
      setEmailError(null);
    },
    onError: (error: Error) => {
      setEmailError(error.message || "Failed to set email notification");
    },
  });

  // Rotate through random statistics every 15 seconds with fade transition
  useEffect(() => {
    if (aiVisibilityStats.length <= 1) return;

    const interval = setInterval(() => {
      // Start fade out
      setIsTransitioning(true);
      
      // After fade out completes, change to a random stat and fade in
      setTimeout(() => {
        setCurrentStatIndex((prev) => getRandomStatIndex(prev));
        setIsTransitioning(false);
      }, 500); // 500ms for fade out duration
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const handleEmailSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setEmailError("Please enter an email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    notifyMutation.mutate(email);
  }, [email, notifyMutation]);

  const currentStat: AiVisibilityStat = aiVisibilityStats[currentStatIndex] || aiVisibilityStats[0];

  // Determine status message
  const getStatusMessage = () => {
    switch (status) {
      case "pending":
        return t("marketing.aiCreditReport.generating.statusPending");
      case "running":
        return t("marketing.aiCreditReport.generating.statusRunning");
      case "processing_discovery":
        return t("marketing.aiCreditReport.generating.statusDiscovery");
      default:
        return t("marketing.aiCreditReport.generating.statusGenerating");
    }
  };

  // Get display progress (use provided values or fall back to status-based defaults)
  const displayPercent = progressPercent ?? (status === 'pending' ? 5 : status === 'running' ? 25 : 50);
  const displayMessage = progressMessage ?? getStatusMessage();

  return (
    <div className="w-full max-w-2xl mx-auto mt-8 space-y-8">
      {/* Progress Section */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <LoaderIcon className="w-10 h-10 animate-spin text-primary" />
        </div>
        
        <div>
          <h2 className="text-2xl font-bold mb-2">
            {t("marketing.aiCreditReport.generating.title")}
          </h2>
          <p className="text-muted-foreground text-lg">
            {domain}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full max-w-md mx-auto space-y-2">
          <div className="relative h-3 bg-muted rounded-full overflow-hidden">
            <div 
              className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-700 ease-out"
              style={{ width: `${displayPercent}%` }}
            />
            {/* Animated shine effect */}
            <div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"
              style={{ width: `${displayPercent}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{displayPercent}% complete</span>
            {timeRemainingDisplay && (
              <span className="flex items-center gap-1">
                <ClockIcon className="w-3 h-3" />
                {timeRemainingDisplay}
              </span>
            )}
          </div>
        </div>

        {/* Current Step Status */}
        <div className="flex flex-col items-center gap-2 text-sm">
          <span className="font-medium text-foreground">{displayMessage}</span>
          {!timeRemainingDisplay && estimate && (
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <ClockIcon className="w-4 h-4" />
              {t("marketing.aiCreditReport.generating.estimatedTime", {
                time: estimate.displayText,
              })}
            </span>
          )}
        </div>
      </div>

      {/* Email Notification Section - Above the fold for better visibility */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <MailIcon className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-1">
              {emailSubmitted 
                ? t("marketing.aiCreditReport.generating.emailSubmitted.title")
                : t("marketing.aiCreditReport.generating.email.title")
              }
            </h3>
            <p className="text-sm text-muted-foreground">
              {emailSubmitted 
                ? t("marketing.aiCreditReport.generating.emailSubmitted.description", { email })
                : t("marketing.aiCreditReport.generating.email.description")
              }
            </p>
          </div>
        </div>

        {!emailSubmitted ? (
          <form onSubmit={handleEmailSubmit} className="space-y-3">
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder={t("marketing.aiCreditReport.generating.email.placeholder")}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                className="flex-1"
                disabled={notifyMutation.isPending}
              />
              <Button 
                type="submit" 
                disabled={notifyMutation.isPending}
              >
                {notifyMutation.isPending 
                  ? t("marketing.aiCreditReport.generating.email.saving")
                  : t("marketing.aiCreditReport.generating.email.notify")
                }
              </Button>
            </div>
            
            {emailError && (
              <p className="text-sm text-destructive">{emailError}</p>
            )}

            <p className="text-xs text-muted-foreground">
              {t("marketing.aiCreditReport.generating.email.privacy")}{" "}
              <a
                href="/legal/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {t("marketing.aiCreditReport.generating.email.privacyLink")}
              </a>
            </p>
          </form>
        ) : (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <CheckCircleIcon className="w-4 h-4" />
            <span>
              {t("marketing.aiCreditReport.generating.emailSubmitted.confirmed")}
            </span>
          </div>
        )}
      </div>

      {/* AI Visibility Statistics - Fading Display */}
      {aiVisibilityStats.length > 0 && (
        <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-6 border border-primary/20">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-primary">
              <span>📊</span>
              <span>{t("marketing.aiCreditReport.generating.didYouKnow")}</span>
            </div>
            
            <div 
              className={`min-h-[140px] transition-opacity duration-500 ease-in-out ${
                isTransitioning ? "opacity-0" : "opacity-100"
              }`}
            >
              <h3 className="text-lg font-semibold text-foreground mb-3">
                {currentStat.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {currentStat.quote}
              </p>
            </div>

            <div 
              className={`flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-primary/10 transition-opacity duration-500 ease-in-out ${
                isTransitioning ? "opacity-0" : "opacity-100"
              }`}
            >
              <span>{currentStat.source} • {currentStat.date}</span>
              <a 
                href={currentStat.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-primary hover:underline"
              >
                {t("marketing.aiCreditReport.generating.readMore")}
                <ExternalLinkIcon className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

